import {
  ChangeDetectionStrategy,
  Component,
  computed,
  signal,
  input,
  output,
  model,
  inject,
  ElementRef,
  viewChild,
  effect,
  untracked,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ToastrService } from 'ngx-toastr';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { Base64Service, FileObject } from 'src/app/services/base64/base64.service';
import { DropZoneDirective } from 'src/app/directives/drop-zone.directive';
import { AngularDeviceInformationService } from 'angular-device-information';
import { CameraComponent } from 'src/app/content/_components/_shared/camera/camera.component';
import { FileForPreview, PreviewThumbnail } from './preview-thumbnail.type';
import dayjs from 'dayjs';
import { type CdkDragDrop, moveItemInArray, CdkDropList, CdkDrag } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { ClarityModule } from '@clr/angular';
import { FileForMediaView, MediaViewModalComponent } from '../mediaviewmodal/mediaviewmodal.component';
import { FormsModule } from '@angular/forms';
import { DotDotDotPipe } from 'src/app/pipes/dot-dot-dot.pipe';

/** For saving the files to server use await packUnsavedFiles() method. */
@Component({
  selector: 'app-preview-thumbnails',
  templateUrl: './preview-thumbnails.component.html',
  styleUrls: ['./preview-thumbnails.component.scss'],
  imports: [
    CommonModule,
    ClarityModule,
    FormsModule,
    CdkDropList,
    CdkDrag,
    MediaViewModalComponent,
    CameraComponent,
    DropZoneDirective,
    MrTranslatePipe,
    DotDotDotPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PreviewThumbnailsComponent {
  private readonly domSanitizer = inject(DomSanitizer);
  private readonly mrTranslate = inject(MrTranslatePipe);
  private readonly toastr = inject(ToastrService);
  private readonly base64service = inject(Base64Service);
  private readonly deviceInformationService = inject(AngularDeviceInformationService);

  //#region public Properties
  /**
   * @description für Verwaltung im Front-End
   * @warning `für Speichern im Back-End nicht geeignet`
   */
  public get rawPreviewThumbnails(): PreviewThumbnail[] {
    return this.previewThumbnails();
  }
  public set rawPreviewThumbnails(value: PreviewThumbnail[]) {
    this.previewThumbnails.set(value);
  }

  public readonly unsaved = computed(() => this.previewThumbnails().filter(file => !file.saved));
  public readonly saved = computed(() => this.previewThumbnails().filter(file => file.saved));
  public readonly savedNames = computed(() =>
    this.saved()
      .map(file => file.name)
      .join('|')
  );

  public readonly isChanged = signal(false);
  //#region @Input
  /**
   * @example [acceptTypes]=''image/*, video/*, .pdf''
   * @example [acceptTypes]=''.png, .svg''
   */
  readonly acceptTypes = input('image/*, video/*');
  /**
   * @example [rejectTypes]=''.svg''
   */
  readonly rejectTypes = input('');
  /** @unit MB */
  readonly maxSize = input<number>(50);
  readonly maxAmount = input<number>(100);
  readonly title = input<string>('Mangelmedien');
  /**
   * @property sperrt Hochladen, Löschen
   * @example <app-preview-thumbnails [readonly]='condition | true'>
   */
  readonly disabled = model(false, { alias: 'readonly' });
  readonly galleryMode = input(true);

  readonly onlyIcons = input<boolean>(false);
  readonly drag_drop = input<boolean>(false);
  readonly drag_move = input<boolean>(false);

  readonly showLabel = input<boolean>(false);
  readonly showCamera = input<boolean>(true);
  readonly isSkizze = input<boolean>(false);
  readonly allowCameraVideo = input<boolean>(true);
  readonly skipSavedOnPack = model<boolean>(true);

  readonly data = model<FileForPreview[]>([]);
  #_setData = effect(() => {
    const files = this.data() ?? [];
    untracked(() => {
      if (files.length) this.setPreviews(files);
      else this.clearPreview();
      this.callbackOnReady?.(this.previewThumbnails());
    });
  });

  /**
   * @alias getBigFileFnc
   * @function base64 beim Server anfordern (mit Dateigröße ab 9,5 MB)
   * @async
   * @returns Promise - string
   */
  readonly fileBase64FromServer = input<(idOrName: number | string) => Promise<string>>(undefined, {
    alias: 'getBigFileFnc',
  });
  /**
   * @function condition für highlight class Anwendung
   * @returns true/false - class active/inactive
   */
  readonly highlightFnc = input((_: string): boolean => false);
  //#endregion
  //#region @Output
  /** @emits name beim Hochladen eines Preview-Thumbnails */
  readonly lastUpload = output<string>();
  /**
   * @emits beim Erreichen maxAmount der Preview-Thumbnail Anzahl
   * @see maxAmount
   */
  readonly reachedMax = output<void>();
  /** @emits `Name` immer beim Löschen einen Preview-Thumbnail */
  readonly clearedThumb = output<string>();
  /**
   * @emits `idOrName`  - Beim Löschen eines Preview-Thumbnails
   * @condition file.saved && file.name
   * @description für Löschen auch auf dem Server/Datenbank
   */
  readonly deleteFile = output<number | string>();
  /**
   * @required if allowTypes includes PDF
   * @emits `File` - für Öffnen mit viewauftragdialog oder custom iframe
   * @example pdfUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL($event))
   * @template <iframe *ngIf='pdfUrl' [attr.src]='pdfUrl' style='border: none; width: 100%; height: 50svh;'></iframe>
   */
  readonly openPdfOverlay = output<File>();
  readonly returnFnc = output<void>();
  //#endregion
  //#endregion
  //#region hidden Properties
  private readonly video = viewChild.required<ElementRef<HTMLVideoElement>>('video');
  private readonly canvas = viewChild.required<ElementRef<HTMLCanvasElement>>('canvas');

  /**
   * @description für Speichern im Back-End
   * @see PreviewThumbnailsComponent.rawPreviewThumbnails - `für Verwaltung im Front-End`
   */
  protected readonly previewThumbnails = signal<PreviewThumbnail[]>([]);
  protected readonly openCameraModal = signal(false);
  protected readonly isWindows = this.deviceInformationService.getDeviceInfo().os === 'Windows';

  private sperrSort = false;
  private callbackOnReady: (previews: PreviewThumbnail[]) => void;
  protected readonly imageMedia = signal<FileForMediaView>(undefined);

  private readonly allowImage = computed(() => this.acceptTypes().includes('image'));
  private readonly allowVideo = computed(() => this.acceptTypes().includes('video'));
  private readonly allowPdf = computed(() => this.acceptTypes().includes('pdf'));
  //#endregion

  //#region public Methods
  public copyFrom(preview: PreviewThumbnailsComponent) {
    this.rawPreviewThumbnails = preview?.rawPreviewThumbnails ?? [];
  }

  public clearPreview() {
    if (!this.data() || this.data().length) {
      this.data.set([]);
      return;
    }
    this.previewThumbnails.set([]);
    this.isChanged.set(false);
  }

  /**
   * @method Erzeugt PreviewThumbnail vom base64 aus dem Back-End
   * @param files - {name: string, base64; string, id?: number}[]
   * @pattern name - `{name}.{extension}`
   */
  public unpackFiles(files: FileForPreview[], callbackOnReady?: (previews: PreviewThumbnail[]) => void) {
    this.data.set(files ?? []);
    this.callbackOnReady = callbackOnReady;
  }

  /**
   * @method Konvertiert File[] zu Back-End geeignete Objekte mit base64, type, addedDate
   * @async
   * @example await this.preview.packAllFiles();
   */
  public packAllFiles(): Promise<FileObject[]> {
    return this.base64service.packFiles(this.previewThumbnails());
  }

  /**
   * @method Konvertiert File[] zu Back-End geeignete Objekte mit base64, type, addedDate
   * @async
   * @example await this.preview.packUnsavedFiles();
   */
  public packUnsavedFiles(): Promise<FileObject[]> {
    return this.base64service.packFiles(this.unsaved());
  }

  /** @method Sets saved = true; für Benutzen nach Speichern im Back-End*/
  public setSaved() {
    this.previewThumbnails.update(
      arr => (
        arr.forEach(thumbnail => {
          thumbnail.saved = true;
          thumbnail.name ??= thumbnail.file?.name;
        }),
        [...arr]
      )
    );
  }

  public forceOpenPdf(index: number) {
    this.openPdfDoc(index);
  }
  //#endregion
  //#region hidden Methods
  /**
   * @method Konvertiert base64 zu File, if kommt vom Server
   * @emits openPdfOverlay(File)
   */
  protected async openPdfDoc(index: number) {
    const fileObj = this.previewThumbnails()[index];
    try {
      //! File
      //* - wenn neu hochgeladen;
      if (!(fileObj.file instanceof File)) {
        //* - aus base64:
        //   - fileObj
        //   - beim Server anfordern, wenn Size mehr als 9,5 MB
        fileObj.base64 ??= await this.fileBase64FromServer()(fileObj.id || fileObj.name);
        fileObj.file = this.base64service.convertFileAsBlob(fileObj.base64, 'application/pdf', fileObj.file.name);
      }
      if (!(fileObj.file instanceof File)) throw new Error('Could not convert to File');
      this.openPdfOverlay.emit(fileObj.file);
    } catch (er) {
      this.toastr.error((er as Error).message);
    }
  }
  /** @method Konvertiert base64 oder File zu URL */
  protected async openVideo(index: number) {
    const fileObj = this.previewThumbnails()[index];
    if (!fileObj.url) {
      try {
        //! url
        //* - vom File, wenn neu hochgeladen;
        if (fileObj.file instanceof File) {
          fileObj.url = URL.createObjectURL(fileObj.file);
          if (!fileObj.url.startsWith('blob:')) fileObj.base64 = fileObj.url.split(',').pop();
        } else {
          //* - aus base64:
          //   - fileObj
          //   - beim Server anfordern, wenn Size mehr als 9,5 MB
          fileObj.base64 ??= await this.fileBase64FromServer()(fileObj.id || fileObj.name);
          fileObj.url = 'data:video/mp4;base64,' + fileObj.base64;
        }
      } catch (er) {
        this.toastr.error((er as Error).message);
      }
    }
  }
  /**
   * @method Erzeugt PreviewThumbnail aus der User-Input;
   * `Image` - sofort und async zu base64 konvertiert
   * @param files - aus der `FileInput` oder `Dropzone` Directive
   * @emits lastUpload(name)
   * @throws maximal erlaubte Anzahl von Files überschritten
   * @throws nicht zugelassene Typ vom File wurde hochgeladen
   * @throws Dateigröße maxSize in MB wurde überschritten
   */
  protected async onFileChange(files: File[]) {
    if (!files.length) return;

    const promisesArray: Promise<void>[] = [];
    let showTypeWarning = false;

    for (const file of files) {
      if (!file) continue;
      if (!this.validAmount(promisesArray.length)) break;
      if (!this.validSize(file.size, file.name)) continue;

      const extension = file.name.split('.').pop().toLowerCase();
      const date = dayjs().format('DD.MM.YYYY HH.mm.ss.SSS');

      const acceptTypes = this.acceptTypes();
      const rejectTypes = this.rejectTypes();
      if (
        file.type.includes('image') &&
        (this.allowImage() || acceptTypes.includes(extension)) &&
        !rejectTypes.includes(extension)
      ) {
        promisesArray.push(
          new Promise<void>(async (resolve, reject) => {
            try {
              const src = await this.base64service.getUrlStringFromFile(file);
              this.previewThumbnails.update(
                thumbs => (
                  thumbs.unshift({
                    src: this.domSanitizer.bypassSecurityTrustUrl(src),
                    base64: src.split(',').pop(), // get rid of data:image/{type};base64,
                    file,
                    extension,
                    date,
                  }),
                  [...thumbs]
                )
              );
              this.lastUpload.emit(file.name);
              this.isChanged.set(true);
              resolve();
            } catch (error) {
              reject(error);
            }
          })
        );
      } else if (
        file.type.includes('video') &&
        (this.allowVideo() || acceptTypes.includes(extension)) &&
        !rejectTypes.includes(extension)
      ) {
        this.previewThumbnails.update(
          thumbs => (
            thumbs.push({
              src: this.domSanitizer.bypassSecurityTrustUrl('/assets/icons/videoicon.png'),
              base64: null,
              file,
              extension,
              date,
              plays: true,
            }),
            [...thumbs]
          )
        );
        this.lastUpload.emit(file.name);
        this.isChanged.set(true);
      } else if (
        file.type == 'application/pdf' &&
        (this.allowPdf() || acceptTypes.includes(extension)) &&
        !rejectTypes.includes(extension)
      ) {
        this.previewThumbnails.update(
          thumbs => (
            thumbs.push({
              src: this.domSanitizer.bypassSecurityTrustUrl('/assets/icons/pdf-file-icon.png'),
              base64: null,
              file,
              extension,
              date,
              opens: true,
            }),
            [...thumbs]
          )
        );
        this.lastUpload.emit(file.name);
        this.isChanged.set(true);
      } else {
        console.log(file.type);
        this.toastr.error(file.name + this.mrTranslate.transform(' kann nicht geladen werden!'));
        showTypeWarning = true;
      }
    }

    if (showTypeWarning) {
      const text = [
        this.allowImage() ? this.mrTranslate.transform('Bilder') : [],
        this.allowVideo() ? this.mrTranslate.transform('Video') : [],
        this.allowPdf() ? this.mrTranslate.transform('PDF Dokumente') : [],
      ]
        .flat()
        .join(` ${this.mrTranslate.transform('oder')} `);
      if (text) this.toastr.warning(this.mrTranslate.transform('Sie können nur {0} hochladen!').replace('{0}', text));
    }

    await Promise.allSettled(promisesArray);
    this.sortFiles();
    this.returnFnc.emit();
  }

  protected onFileInputChange(event: Event) {
    if ('files' in event.target) this.onFileChange(Array.from((event.target as HTMLInputElement).files));
  }

  protected moveInArray(event: CdkDragDrop<PreviewThumbnail[]>) {
    const previews = this.previewThumbnails();
    moveItemInArray(previews, event.previousIndex, event.currentIndex);
    this.previewThumbnails.set([...previews]);
  }

  protected openDoc(index: number) {
    const thumb = this.previewThumbnails()[index];
    if (thumb.opens || thumb.plays) return;

    this.imageMedia.set({
      isSkizze: this.isSkizze(),
      title: this.isSkizze() ? 'Messpunktskizze' : 'Passbild',
      files: this.previewThumbnails(),
      currentFile: thumb,
    });
  }

  protected openImages() {
    const thumb = this.previewThumbnails()[0];
    this.imageMedia.set({
      deleteable: true,
      isSkizze: false,
      title: this.mrTranslate.transform(this.title()),
      files: this.previewThumbnails(),
      currentFile: thumb,
    });
  }

  /**
   * @method Löscht previewThumbnail aus dem Array
   * @emits deletedFiles(idOrName) - wenn saved && name
   * @emits clearedThumb()
   */
  protected deletePreview(index: number) {
    const thumb = this.previewThumbnails()[index];
    if (thumb.saved) {
      if (thumb.name) {
        this.deleteFile.emit(thumb.id || thumb.name);
      } else {
        this.toastr.error(this.mrTranslate.transform('Dateien können nach dem erneuten Öffnen gelöscht werden!'));
        return;
      }
    }
    this.isChanged.set(true);
    this.previewThumbnails.update(thumbs => (thumbs.splice(index, 1), [...thumbs]));
    this.clearedThumb.emit(thumb.name);
  }

  protected async onTakenVideo(file: File) {
    if (!this.validAmount()) return;
    if (!this.validSize(file.size, file.name)) return;

    const extension = file.name.split('.').pop().toLowerCase();
    const date = dayjs().format('DD.MM.YYYY HH.mm.ss.SSS');
    const url = URL.createObjectURL(file);
    const src = this.onlyIcons() ? null : this.domSanitizer.bypassSecurityTrustUrl(await this.createThumbnail(url, 1));

    this.previewThumbnails.update(
      thumbs => (
        thumbs.push({
          src,
          base64: null,
          file,
          extension,
          url,
          date,
          plays: true,
        }),
        [...thumbs]
      )
    );
    this.isChanged.set(true);
    this.lastUpload.emit(file.name);
    this.toastr.info(this.mrTranslate.transform('Video wurde hinzugefügt!'));
  }

  /**
   * @method Erzeugt PreviewThumbnail aus der Camera-Input;
   * @param urlString - aus der Camera Modal
   * @pattern urlString = `data:{image | video}/{type};base64,{base64}`
   * @throws maximal erlaubte Anzahl von Files überschritten
   */
  protected onTakenFoto(urlString: string) {
    if (!this.validAmount()) return;

    const date = dayjs().format('DD.MM.YYYY HH.mm.ss.SSS');
    const name = `${date}.jpeg`;
    const base64 = urlString.split(',').pop(); // get rid of data:image/{type};base64,
    const file = this.base64service.convertFileAsBlob(base64, 'image/jpeg', name);
    if (!this.validSize(file.size)) return;

    this.previewThumbnails.update(
      thumbs => (
        thumbs.unshift({
          src: this.domSanitizer.bypassSecurityTrustUrl(urlString),
          base64,
          file,
          extension: 'jpeg',
          date,
        }),
        [...thumbs]
      )
    );
    this.isChanged.set(true);
    this.lastUpload.emit(name);
    this.toastr.info(this.mrTranslate.transform('Foto wurde hinzugefügt!'));
  }

  private setPreviews(files: FileForPreview[]) {
    const previews = files
      .filter(p => p != null)
      .map(({ id, name, base64, extension }) => {
        extension ??= name?.split('.').pop().toLowerCase() || 'png';
        let opens = false;
        let plays = false;
        let type = 'image/';
        let src = '';
        switch (extension) {
          case 'svg':
            src = 'data:image/' + extension + '+xml;base64,' + base64;
            break;
          case 'mp4':
          case 'mov':
            src = '/assets/icons/videoicon.png';
            plays = true;
            type = 'video/' + extension;
            break;
          case 'pdf':
            src = '/assets/icons/pdf-file-icon.png';
            opens = true;
            type = 'application/pdf';
            break;

          default:
            src = 'data:image/' + extension + ';base64,' + base64;
            type += extension;
            break;
        }
        const preview: PreviewThumbnail = {
          src: this.domSanitizer.bypassSecurityTrustUrl(src),
          base64,
          file: { name: name?.split('$').pop(), type },
          extension,
          saved: this.skipSavedOnPack() ? true : false,
          id,
          name, // for id im Server
          plays,
          opens,
          highlight: this.highlightFnc()(name),
        };

        return preview;
      });

    this.previewThumbnails.set(previews);
    this.sortFiles();
    this.isChanged.set(false);
  }

  /** @sort Files Bilder -> Video -> PDF -> Highlight*/
  private sortFiles() {
    if (this.sperrSort) return;
    this.previewThumbnails.update(
      thumbs => (
        thumbs.sort(
          (
            { opens: opensA = false, plays: playsA = false, highlight: highlightA = false },
            { opens: opensB = false, plays: playsB = false, highlight: highlightB = false }
          ) => {
            // * Beide sind gleich Typ
            if (opensA == opensB && playsA == playsB && highlightA == highlightB) return 0;

            // * Highlight am Ende
            if (highlightA) return 1;

            // * PDF nach Bilder und nach Video, aber vor Highlight
            if (opensA && !highlightB) return 1;

            // * Video nach Bilder, aber vor PDF
            if (playsA && !opensB) return 1;

            return -1;
          }
        ),
        [...thumbs]
      )
    );
  }

  /**
   * @method Checks if maxAmount noch nicht erreicht wurde
   * @param pendingLength - aus OnFileChange (es gibt Promise für `Image`, aber Array hat noch kein Objekt)
   * @returns true - if Hochladen noch erlaubt
   * @emits reachedMax - if Hochladen nicht mehr erlaubt
   */
  private validAmount(pendingLength: number = 0) {
    const valid = pendingLength + this.previewThumbnails().length < this.maxAmount();
    if (!valid) {
      this.toastr.warning(
        this.mrTranslate.transform('Sie können keine Medien mehr hinzufügen'),
        this.mrTranslate.transform('Maximale Anzahl') + ': ' + this.maxAmount()
      );
      this.reachedMax.emit();
    }
    return valid;
  }

  /**
   * @method Checks if unter maxSize
   * @returns true - if Hochladen erlaubt
   */
  private validSize(size: number, name?: string) {
    const valid = size / 1024 / 1024 < this.maxSize();
    if (!valid) {
      this.toastr.warning(
        name ?? this.mrTranslate.transform('Datei kann nicht hinzugefügt werden'),
        `${this.mrTranslate.transform('Maximale Größe')}: ${this.maxSize()} MB`
      );
    }
    return valid;
  }

  private async createThumbnail(videoSrc: string, time = 1): Promise<string> {
    const video = this.video().nativeElement;
    const canvas = this.canvas().nativeElement;
    const ctx = canvas.getContext('2d')!;

    video.src = videoSrc;
    video.crossOrigin = 'anonymous';

    await video.play().catch(() => {}); // required for Safari
    video.pause();

    video.currentTime = time;

    await new Promise<void>(resolve => (video.onseeked = () => resolve()));

    canvas.width = 150;
    canvas.height = 84.375;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', 0.85);
  }
  //#endregion
}
